import { useUser } from '@clerk/clerk-react';
import { Button } from '@vrplatform/components';
import {
  useApiEndpoint,
  useDashboard,
  useMe,
  useOwnerPortalContact,
  useTeam,
  useTrackingSignOut,
} from '@vrplatform/data';
import { theme as defaultTheme } from '@vrplatform/theme';
import {
  LoadingOverlay,
  MantineThemeProvider as MantineProvider,
  Stack,
  createTheme,
} from '@mantine/core';
import { VRP_TENANT_ID, getOwnerPortalColor } from '@vrplatform/ui-common';
import { type ReactNode, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import {
  AddTeamDrawer,
  AddUserAccessDrawer,
  AutomationHistoryDrawer,
  BankRecordDetailDrawer,
  BankTransferDetailDrawer,
  ConnectionDetailDrawer,
  DepositDetailDrawer,
  ExpenseDetailDrawer,
  LegacyOwnerDetailDrawer,
  OwnerDetailDrawer,
  PaymentDetailDrawer,
  ReservationDetailDrawer,
  PayoutDetailDrawer,
  UserAccessDetailDrawer,
} from '../drawers';
import { AppLayout } from '../layout/AppLayout';
import { TeamSwitch } from '../layout/navbar/_components/NavbarTeamSwitch';
import {
  FinalyticMigrationModal,
  OwnerInviteLoggedInModal,
  RunModal,
  WelcomeUserModal,
} from '../modals';
import {
  OwnerNavigation,
  PartnerNavigation,
  PmNavigation,
  SettingsNavigation,
} from '../navigation';
import { VrpNavigation } from '../navigation/VrpNavigation';
import { ListingActivityDrawer } from '../views/listings/activity';
import { ListingDetailDrawer } from '../views/listings/drawer';
import { OnboardingRoutes } from '../views/onboarding';
import { OwnerActivityDrawer } from '../views/owners/legacy/owners/activity';
import EmptyTeamView from '../views/setup-migration/EmptyTeamView';
import { DashboardRoutes } from './dashboard';
import { EmptyViewWrapper, FullPageLoadingIndicator } from '../components';
import { resolveUserLoadedViewState } from './AppRoutes.utils';

export const AppRoutes = () => {
  const [team, , isFetchingTeam] = useTeam();

  return (
    <>
      {/** biome-ignore lint/correctness/useUniqueElementIds: static id is fine */}
      <LoadingOverlay
        visible={isFetchingTeam && !!team?.id}
        opacity={0.5}
        id="loading-overlay"
        overlayProps={{ radius: 'sm', blur: 2, color: '#000' }}
        loaderProps={{
          color: '#000',
        }}
      />
      <RouteSwitch />
    </>
  );
};

const RouteSwitch = () => {
  const user = useMe();

  // If user hasn't loaded => show loading
  if (!user?.id) {
    return <LoadingSubscription />;
  }

  return <UserLoaded />;
};

type TeamLoadErrorActionsProps = {
  canResetApiHost: boolean;
  onRefresh: () => void;
  onResetApiHost: () => void;
  onSignOut: () => void;
  teamSwitch?: ReactNode;
};

export const TeamLoadErrorActions = ({
  canResetApiHost,
  onRefresh,
  onResetApiHost,
  onSignOut,
  teamSwitch,
}: TeamLoadErrorActionsProps) => {
  return (
    <Stack gap="sm">
      {canResetApiHost && (
        <Button onClick={onResetApiHost} variant="primary">
          Use default API
        </Button>
      )}
      <Button
        variant={canResetApiHost ? 'default' : 'primary'}
        onClick={onRefresh}
      >
        Refresh
      </Button>
      {teamSwitch || (
        <TeamSwitch
          header={null}
          hideWhenUnavailable
          isDesktopExpanded
          renderTrigger={() => (
            <Button sx={{ width: '100%' }} variant="default">
              Switch to another team
            </Button>
          )}
          tone="default"
        />
      )}
      <Button onClick={onSignOut}>Sign out</Button>
    </Stack>
  );
};

const ErrorView = ({ error }: { error: Error | null | undefined }) => {
  const [, refreshTeam] = useTeam();
  const signOut = useTrackingSignOut();
  const { apiHost, setApiHost, storedApiHost } = useApiEndpoint();
  const isLocalApiHost = apiHost.includes('localhost');
  const canResetApiHost = isLocalApiHost || !!storedApiHost;
  const description = isLocalApiHost
    ? `Could not reach the local API at ${apiHost}. Start it locally or switch back to the default API.`
    : error?.message ||
      'Please try again and contact support if the issue persists';

  return (
    <EmptyViewWrapper
      title="Failed to load team"
      description={description}
      actions={
        <TeamLoadErrorActions
          canResetApiHost={canResetApiHost}
          onRefresh={() => {
            void refreshTeam();
          }}
          onResetApiHost={() => setApiHost(undefined)}
          onSignOut={() => {
            void signOut({ trackEvent: false });
          }}
        />
      }
    />
  );
};

const UserLoaded = () => {
  const user = useMe();
  const [team, , isFetchingTeam, , teamQueryError] = useTeam();
  const location = useLocation();
  const viewState = resolveUserLoadedViewState({
    teamId: team?.id,
    isFetchingTeam,
    teamQueryError,
    isOnboarding: !!team?.isOnboarding,
    pathname: location.pathname,
    canAccessTeam: user.canAccessTeam,
  });

  switch (viewState) {
    case 'loading':
      return <FullPageLoadingIndicator message="Loading Dashboard..." />;
    case 'error':
      return <ErrorView error={teamQueryError} />;
    case 'empty-team':
      return <EmptyTeamView />;
    case 'onboarding':
      return <Onboarding />;
    case 'dashboard':
      return <Dashboard />;
  }

  throw new Error(`Unsupported user loaded view state: ${viewState}`);
};

const Onboarding = () => {
  return (
    <>
      <OnboardingRoutes />
      <WelcomeUserModal />
      <FinalyticMigrationModal />
    </>
  );
};

const Dashboard = () => {
  const [dashboard] = useDashboard();

  if (dashboard === 'owner') {
    return <OwnerPortal />;
  }

  return <App />;
};

function OwnerPortal() {
  const [team] = useTeam();
  const primaryColor = getOwnerPortalColor(team.colorPrimary);

  const theme = useMemo(() => {
    if (!primaryColor) return undefined;

    return createTheme({
      ...defaultTheme,
      primaryColor,
    });
  }, [primaryColor]);

  if (!theme) return <App />;

  return (
    <MantineProvider theme={theme}>
      <App />
    </MantineProvider>
  );
}

function App() {
  const [dashboard] = useDashboard();
  const location = useLocation();
  const [{ id: teamId, isGL: GL }] = useTeam();
  const { contactId } = useOwnerPortalContact();

  const DashboardNavigation = useMemo(() => {
    if (dashboard === 'owner') return OwnerNavigation;
    if (dashboard === 'partner')
      return teamId === VRP_TENANT_ID ? VrpNavigation : PartnerNavigation;
    if (dashboard === 'overview') return PartnerNavigation;
    return PmNavigation;
  }, [dashboard, teamId]);

  const isSettingsView = location.pathname.startsWith('/settings');

  if (!contactId && dashboard === 'owner') {
    return <DashboardRoutes />;
  }

  return (
    <>
      <AppLayout
        navigation={isSettingsView ? SettingsNavigation : DashboardNavigation}
        drawers={[
          AutomationHistoryDrawer,
          RunModal,
          ConnectionDetailDrawer,
          PaymentDetailDrawer,
          ReservationDetailDrawer,
          GL ? OwnerDetailDrawer : LegacyOwnerDetailDrawer,
          ListingDetailDrawer,
          AddUserAccessDrawer,
          UserAccessDetailDrawer,
          AddTeamDrawer,
          OwnerActivityDrawer,
          ListingActivityDrawer,
          ExpenseDetailDrawer,
          DepositDetailDrawer,
          BankRecordDetailDrawer,
          PayoutDetailDrawer,
          BankTransferDetailDrawer,
        ]}
      >
        <DashboardRoutes />
      </AppLayout>
      <WelcomeUserModal />

      {/*<OwnerTaxInfoModal />*/}
      <OwnerInviteLoggedInModal />
      <FinalyticMigrationModal />
    </>
  );
}

const LoadingSubscription = () => {
  const [{ id: teamId }] = useTeam();
  const [, refreshTeam] = useTeam();
  const { user } = useUser();

  useEffect(() => {
    const interval = setInterval(async () => {
      await user?.reload(); // refetch on interval clerk public metadata with tenant_ids
      if (teamId || user?.publicMetadata?.user_id) refreshTeam();
    }, 3000);

    return () => clearInterval(interval);
  }, [teamId, refreshTeam, user]);

  return <FullPageLoadingIndicator message="Loading Dashboard..." />;
};
